import type { RoleSummary } from './types';

/**
 * One line per seeded role, phrased as what a holder does. Seeded names such as `GammaAdmin`
 * say nothing on their own, and picking a role to assign is otherwise guesswork. Keyed by
 * `systemKey` because names are editable rows while the key is fixed.
 */
const SYSTEM_ROLE_DESCRIPTIONS: Record<string, string> = {
  Owner: 'Everything Admin has, plus approval bypass. Only its holder can give it up.',
  Admin: 'Runs the organisation: team, roles, live funds and every chat topic.',
  Approver: 'Manages funds, deals and the profile, and approves role requests.',
  Initiator: 'Creates and edits funds and deals, and works the investing board.',
  Viewer: 'Sees the organisation, its funds and chats. Cannot change anything.',
  GammaSuperAdmin: 'Everything, including the Gamma team and role administration.',
  GammaAdmin: 'Runs the platform: reviews, deletions, moderation and maintenance.',
  GammaApprover: 'Reviews entities, funds and KYC, and approves role requests.',
  GammaInitiator: 'Prepares changes to funds, users, deals and documents.',
  GammaViewer: 'Read-only access to the admin views.',
  User: 'The default for every platform user. Managed on the Gamma Team page.',
  HostAdmin: 'Hosts events. Managed on the Gamma Team page.',
};

export const systemRoleDescription = (role: Pick<RoleSummary, 'systemKey'>): string | null =>
  role.systemKey ? (SYSTEM_ROLE_DESCRIPTIONS[role.systemKey] ?? null) : null;

const pluralise = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const formatPermissionCount = (count: number) => pluralise(count, 'permission');

export const formatMemberCount = (count: number) => pluralise(count, 'member');

/**
 * The secondary line under a role in the list: the seeded description, otherwise how much the
 * custom role grants once its detail has loaded.
 */
export const roleSubtitle = (
  role: Pick<RoleSummary, 'systemKey'>,
  permissionCount: number | undefined,
): string | null =>
  systemRoleDescription(role) ??
  (permissionCount === undefined ? null : formatPermissionCount(permissionCount));
